<script setup>
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import OverviewReportFilters from './OverviewReportFilters.vue';
import SummaryEntityFilters from './SummaryEntityFilters.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import {
  generateReportURLParams,
  generateSummaryEntityFilterParams,
} from '../helpers/reportFilterHelper';

defineProps({
  type: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hideLabelFilter: {
    type: Boolean,
    default: false,
  },
  hideAgentFilter: {
    type: Boolean,
    default: false,
  },
  hideTeamFilter: {
    type: Boolean,
    default: false,
  },
  hideInboxFilter: {
    type: Boolean,
    default: false,
  },
  hideResolutionMethod: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filtersApply']);

const route = useRoute();
const router = useRouter();

const pendingDate = ref(null);
const pendingEntity = ref(null);
const dateReady = ref(false);
const entityReady = ref(false);
const initialFlushed = ref(false);

const updateURLParams = () => {
  const date = pendingDate.value;
  const entity = pendingEntity.value;
  if (!date || !entity) return;

  const dateParams = generateReportURLParams({
    from: date.from,
    to: date.to,
    businessHours: date.businessHours,
    comparisonPeriods: date.comparisonPeriods || [],
  });
  const entityParams = generateSummaryEntityFilterParams(entity);
  const nextQuery = { ...route.query, ...dateParams, ...entityParams };

  if (!date.comparisonPeriods?.length) {
    delete nextQuery.comparison_periods;
  }

  [
    'agent_ids',
    'inbox_ids',
    'team_ids',
    'label_ids',
    'exclude_label_ids',
    'channel_types',
    'episode_result',
    'receipt_type',
    'tag_presence',
    'resolution_method',
    'status_at_period_end',
    'reopening',
    'transfers',
    'human_response',
  ].forEach(key => {
    if (!entityParams[key]) delete nextQuery[key];
  });

  router.replace({ query: nextQuery });
};

const flush = () => {
  if (!pendingDate.value || !pendingEntity.value) return;
  updateURLParams();
  emit('filtersApply', {
    date: pendingDate.value,
    entity: pendingEntity.value,
  });
};

const maybeFlushInitial = () => {
  if (initialFlushed.value) return;
  if (!dateReady.value || !entityReady.value) return;
  initialFlushed.value = true;
  flush();
};

const onDateChange = filters => {
  pendingDate.value = filters;
  dateReady.value = true;
  maybeFlushInitial();
};

const onEntityChange = filters => {
  pendingEntity.value = filters;
  entityReady.value = true;
  maybeFlushInitial();
};
</script>

<template>
  <div class="flex w-full flex-col">
    <slot name="date" :disabled="disabled" :on-date-change="onDateChange">
      <OverviewReportFilters
        :disabled="disabled"
        :sync-url="false"
        @filter-change="onDateChange"
      />
    </slot>
    <SummaryEntityFilters
      :type="type"
      :disabled="disabled"
      :sync-url="false"
      :hide-label-filter="hideLabelFilter"
      :hide-agent-filter="hideAgentFilter"
      :hide-team-filter="hideTeamFilter"
      :hide-inbox-filter="hideInboxFilter"
      :hide-resolution-method="hideResolutionMethod"
      @filter-change="onEntityChange"
    />
    <slot />
    <div class="mt-3">
      <V4Button
        :label="$t('REPORT.FILTER_ACTIONS.APPLY')"
        size="sm"
        :disabled="disabled || !initialFlushed"
        @click="flush"
      />
    </div>
  </div>
</template>
